<template>
    <div v-if="readonly">
        <label :for="`${baseName}_district`">{{ $t('District') }}</label>
        <input :id="`${baseName}_district`" type="text" :value="defaultValue?.district?.name" disabled/>
    </div>
    <div v-else>
        <label :for="`${baseName}_district`">{{ $t('District') }}</label>
        <multi-select
            :id="`${baseName}_district`"
            v-model="district"
            label="name"
            track-by="id"
            :placeholder="$t('-- Select --')"
            :options="districtOpts"
            :select-label="$t('Select')"
            :selected-label="$t('Selected')"
            :deselect-label="$t('Remove')"
            :required="required"
            @select="fetchMunicipalities"
        >
            <template v-slot:noResult>{{ $t('No results found') }}</template>
            <template v-slot:noOptions>{{ $t('List is empty') }}</template>
        </multi-select>
        <input type="hidden" :name="`${baseName}[district_id]`" :value="district?.id" />
    </div>

    <div v-if="readonly">
        <label :for="`${baseName}_municipality`">{{ $t('Municipality') }}</label>
        <input :id="`${baseName}_municipality`" type="text" :value="defaultValue?.municipality?.name" disabled/>
    </div>
    <div v-else>
        <label :for="`${baseName}_municipality`">{{ $t('Municipality') }}</label>
        <multi-select
            :id="`${baseName}_municipality`"
            v-model="municipality"
            label="name"
            track-by="id"
            :placeholder="$t('-- Select --')"
            :options="municipalityOpts"
            :select-label="$t('Select')"
            :selected-label="$t('Selected')"
            :deselect-label="$t('Remove')"
            :required="required"
            @select="fetchParishes"
        >
            <template v-slot:noResult>{{ $t('No results found') }}</template>
            <template v-slot:noOptions>{{ $t('List is empty') }}</template>
        </multi-select>
        <input type="hidden" :name="`${baseName}[municipality_id]`" :value="municipality?.id" />
    </div>

    <div v-if="readonly">
        <label :for="`${baseName}_parish`">{{ $t('Parish') }}</label>
        <input :id="`${baseName}_parish`" type="text" :value="defaultValue?.parish?.name" disabled/>
    </div>
    <div v-else>
        <label :for="`${baseName}_parish`">{{ $t('Parish') }}</label>
        <multi-select
            :id="`${baseName}_parish`"
            v-model="parish"
            label="name"
            track-by="id"
            :placeholder="$t('-- Select --')"
            :options="parishOpts"
            :select-label="$t('Select')"
            :selected-label="$t('Selected')"
            :deselect-label="$t('Remove')"
            :required="required"
            :disabled="municipality === null"
            @select="fetchParishes"
        >
            <template v-slot:noResult>{{ $t('No results found') }}</template>
            <template v-slot:noOptions>{{ $t('List is empty') }}</template>
        </multi-select>
        <input type="hidden" :name="`${baseName}[parish_id]`" :value="parish?.id" />
    </div>
</template>

<script setup>
import { ref, onBeforeMount, watch } from "vue";

import Api from "@/api";
import MultiSelect from "vue-multiselect";

const props = defineProps({
    defaultValue: {
        type: Object,
        required: true,
    },
    errors: {
        type: Object,
        required: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    baseName: {
        type: String,
        default: "location",
    },
    readonly: {
        type: Boolean,
        default: () => false,
    },
});

let watchEnabled = false;

const district = ref(null);
const municipality = ref(null);
const parish = ref(null);
const districtOpts = ref([]);
const municipalityOpts = ref([]);
const parishOpts = ref([]);

const fetchDistricts = async () => {
    const { data } = await Api.request(`/taxonomies/district/list`);
    districtOpts.value = data;
};

const fetchMunicipalities = async () => {
    if(!district.value?.id) return;

    const { data } = await Api.request(`/taxonomies/municipality/list`, {
        district_id: district.value.id
    });

    municipalityOpts.value = data;
};

const fetchParishes = async () => {
    if(!municipality.value?.id) return;

    const { data } = await Api.request(`/taxonomies/parish/list`, {
        municipality_id: municipality.value.id
    });

    parishOpts.value = data;
};

watch(district, () => { if (watchEnabled) { municipality.value = null; parish.value = null; }});
watch(municipality, () => { if (watchEnabled) { parish.value = null; }});

onBeforeMount(async () => {
    await fetchDistricts();

    if (props.defaultValue?.district_id) {
        district.value = districtOpts.value.find(option => option.id === parseInt(props.defaultValue.district_id));
        await fetchMunicipalities();
    }

    if (props.defaultValue?.municipality_id) {
        municipality.value = municipalityOpts.value.find(option => option.id === parseInt(props.defaultValue.municipality_id));
        await fetchParishes();
    }

    if (props.defaultValue?.parish_id) {
        parish.value = parishOpts.value.find(option => option.id === parseInt(props.defaultValue.parish_id));
    }

    watchEnabled = true;
});
</script>

